<template>
    <div class="award-category-container">
        <v-expansion-panel-header class="touch-friendly-header">
            <div class="d-flex align-center">
                <div class="mr-3 touch-checkbox">
                    <v-checkbox
                        :input-value="Boolean(resultCategory.awarded_at)"
                        hide-details
                        color="success"
                        @change="markResultCategoryAwarded"
                        @click.native.stop
                    />
                </div>
                <div class="category-info">
                    <div class="d-flex align-center flex-wrap">
                        <span class="mr-2 category-title">{{ resultCategory.description() }}</span>

                        <!-- Simplify status chip - show only one indicator -->
                        <v-chip
                            x-small
                            :color="resultCategory.awarded_at ? 'success' : 'grey'"
                            text-color="white"
                            class="mr-2"
                        >
                            {{ resultCategory.awarded_at ? 'Awarded' : 'Pending' }}
                        </v-chip>

                        <!-- Remove redundant badge when using the chip above -->
                    </div>

                    <!-- Keep progress bar but make it more subtle -->
                    <v-progress-linear
                        v-if="!resultCategory.awarded_at"
                        :value="getAwardProgressPercentage"
                        height="4"
                        rounded
                        class="mt-2 award-progress"
                        color="info"
                        background-opacity="0.1"
                    ></v-progress-linear>
                </div>
            </div>
        </v-expansion-panel-header>

        <v-expansion-panel-content>
            <!-- Simplified controls -->
            <v-card outlined class="mb-4 pa-3">
                <div class="d-flex flex-wrap justify-space-between align-center">
                    <div>
                        <div class="d-flex align-center mb-2">
                            <span class="text-subtitle-1">Ceremony Status</span>
                            <v-chip small class="ml-2" :color="statusColor">{{ statusText }}</v-chip>
                        </div>
                        <div class="d-flex align-center">
                            <span class="caption mr-2">Award Progress:</span>
                            <span class="caption">{{ getAwardProgressPercentage }}%</span>
                        </div>
                    </div>

                    <div>
                        <v-btn
                            color="success"
                            :disabled="isAwarded || loading"
                            @click="markResultCategoryAwarded"
                            class="action-btn mt-2 px-4 py-2"
                        >
                            <v-icon left>mdi-trophy</v-icon>
                            Complete Division
                        </v-btn>
                    </div>
                </div>
            </v-card>

            <!-- Awarded message - simplified -->
            <div v-if="resultCategory.awarded_at" class="awarded-message pa-3 mb-3 text-center">
                <span class="success--text">This division has been awarded</span>
                <v-icon color="success" small class="ml-1">mdi-check</v-icon>
            </div>

            <!-- Restored athlete data table with simplified design -->
            <v-data-table
                :headers="headers"
                :items="filteredResults"
                :items-per-page="-1"
                hide-default-footer
                class="award-table elevation-1 mb-3"
                :class="{ 'awarded-table': resultCategory.awarded_at }"
            >
                <template #item.placement="{ item: result }">
                    <span class="placement-number">{{ result.latest_round_placement || '-' }}</span>
                </template>

                <template #item.name="{ item: result }">
                    {{ result.athlete?.name || 'Unknown Athlete' }}
                </template>

                <template #item.checked_in="{ item: result }">
                    <v-checkbox
                        :disabled="checkboxDisabled === result.id || isAwarded"
                        :input-value="Boolean(result.award_prepared_at)"
                        color="primary"
                        hide-details
                        class="touch-checkbox mx-auto"
                        @change="(value) => markResult(result, 'award_prepared', value)"
                    />
                </template>

                <template #item.awarded="{ item: result }">
                    <v-checkbox
                        :disabled="checkboxDisabled === result.id || isAwarded"
                        :input-value="Boolean(result.awarded_at)"
                        color="success"
                        hide-details
                        class="touch-checkbox mx-auto"
                        @change="(value) => markResult(result, 'awarded', value)"
                    />
                </template>
            </v-data-table>
        </v-expansion-panel-content>

        <v-snackbar v-model="snackbar.show" :color="snackbar.color" timeout="3000" bottom>
            {{ snackbar.text }}
            <template #action="{ attrs }">
                <v-btn text v-bind="attrs" @click="snackbar.show = false" class="pa-3">
                    <v-icon left>mdi-close</v-icon>
                    Close
                </v-btn>
            </template>
        </v-snackbar>
    </div>
</template>

<script>
import ResultCategory from '@/Models/ResultCategory.js';
import Result from '@/Models/Result.js';

export default {
    name: 'AwardResultCategory',

    components: {},

    props: {
        resultCategory: {
            type: ResultCategory,
            required: true,
        },
    },

    emits: ['saving'],

    data: () => ({
        checkboxDisabled: null,
        loading: false,
        snackbar: {
            show: false,
            text: '',
            color: 'info',
        },

        headers: [
            {
                text: '#',
                sortable: false,
                value: 'placement',
                width: '8%',
            },
            {
                text: 'Name',
                sortable: false,
                value: 'name',
                width: '54%',
            },
            {
                text: 'Checked In',
                sortable: false,
                value: 'checked_in',
                width: '19%',
                align: 'center',
            },
            {
                text: 'Awarded',
                sortable: false,
                value: 'awarded',
                width: '19%',
                align: 'center',
            },
        ],
    }),

    computed: {
        allCheckedIn() {
            return (
                this.resultCategory.results.length > 0 &&
                this.resultCategory.results.every((result) => Boolean(result.award_prepared_at))
            );
        },

        allAwarded() {
            return (
                this.resultCategory.results.length > 0 &&
                this.resultCategory.results.every((result) => Boolean(result.awarded_at))
            );
        },

        someCheckedIn() {
            if (this.allCheckedIn || this.resultCategory.results.length === 0) {
                return false;
            }

            return this.resultCategory.results.some((result) => Boolean(result.award_prepared_at));
        },

        someAwarded() {
            if (this.allAwarded || this.resultCategory.results.length === 0) {
                return false;
            }

            return this.resultCategory.results.some((result) => Boolean(result.awarded_at));
        },

        checkedInCount() {
            return this.resultCategory.results.filter((result) => Boolean(result.award_prepared_at)).length;
        },

        awardedCount() {
            return this.resultCategory.results.filter((result) => Boolean(result.awarded_at)).length;
        },

        progressPercentage() {
            if (this.resultCategory.results.length === 0) return 0;
            return (this.awardedCount / this.resultCategory.results.length) * 100;
        },

        progressColor() {
            if (this.progressPercentage === 100) return 'success';
            if (this.progressPercentage > 50) return 'primary';
            if (this.progressPercentage > 0) return 'warning';
            return 'grey';
        },

        statusText() {
            if (this.resultCategory.awarded_at) return 'COMPLETED';
            if (this.awardedCount === 0) return 'NOT STARTED';
            if (this.allAwarded) return 'READY';
            return 'IN PROGRESS';
        },

        statusColor() {
            if (this.resultCategory.awarded_at) return 'success';
            if (this.awardedCount === 0) return 'grey';
            if (this.allAwarded) return 'info';
            return 'warning';
        },

        filteredResults() {
            if (!this.resultCategory.results || this.resultCategory.results.length === 0) {
                return [];
            }

            // Get the award count based on settings (to be fetched from parent)
            const awardCount = this.getAwardCount();

            // Sort the results by placement
            return this.resultCategory.results
                .slice() // Create a copy to avoid mutating the original
                .sort((a, b) => {
                    // Sort by placement logic
                    const aPlacement = a.latest_round_placement || Number.MAX_SAFE_INTEGER;
                    const bPlacement = b.latest_round_placement || Number.MAX_SAFE_INTEGER;
                    return aPlacement - bPlacement;
                })
                .filter((_, index) => index < awardCount); // Only show the top athletes based on award count
        },

        isAwarded() {
            return Boolean(this.resultCategory && this.resultCategory.awarded_at);
        },

        getAwardProgressPercentage() {
            // Calculate the percentage of athletes who have been awarded in this category
            if (!this.resultCategory || !this.resultCategory.results || this.resultCategory.results.length === 0) {
                return 0;
            }

            const awardedCount = this.resultCategory.results.filter((result) => result.awarded_at).length;
            return Math.round((awardedCount / this.resultCategory.results.length) * 100);
        },
    },

    methods: {
        async checkAll(field, value) {
            this.loading = true;
            this.$emit('saving', true);
            this.checkboxDisabled = field;

            try {
                await Result.api().post('/api/awards', {
                    meet_id: this.resultCategory.meet_id,
                    model_type: 'App\\Models\\Result',
                    model_ids: this.resultCategory.results.map((result) => result.id),
                    field,
                    value,
                });

                // Update local state
                this.resultCategory.results.forEach((result) => {
                    if (field === 'award_prepared') {
                        result.award_prepared_at = value ? new Date() : null;
                    } else if (field === 'awarded') {
                        result.awarded_at = value ? new Date() : null;
                    }
                });

                this.snackbar = {
                    show: true,
                    text: `All athletes ${value ? '' : 'un'}marked as ${field === 'award_prepared' ? 'checked in' : 'awarded'}`,
                    color: 'success',
                };
            } catch (error) {
                console.error(error);
                this.snackbar = {
                    show: true,
                    text: `Error updating athlete statuses`,
                    color: 'error',
                };
            } finally {
                this.checkboxDisabled = null;
                this.loading = false;
                this.$emit('saving', false);
            }
        },

        async markResult(result, field, value) {
            // Ensure value is a boolean
            const boolValue = typeof value === 'object' ? !result[field + '_at'] : Boolean(value);

            // Save current ID to prevent double-clicks
            this.checkboxDisabled = result.id;
            this.loading = true;
            this.$emit('saving', true);

            try {
                await Result.api().post('/api/awards', {
                    meet_id: this.resultCategory.meet_id,
                    model_type: 'App\\Models\\Result',
                    model_ids: [result.id], // Only send the specific result ID
                    field,
                    value: boolValue,
                });

                // Update local state for this specific result
                if (field === 'award_prepared') {
                    result.award_prepared_at = boolValue ? new Date() : null;
                } else if (field === 'awarded') {
                    result.awarded_at = boolValue ? new Date() : null;
                }

                this.snackbar = {
                    show: true,
                    text: `${result.athlete?.name || 'Athlete'} ${boolValue ? '' : 'un'}marked as ${field === 'award_prepared' ? 'checked in' : 'awarded'}`,
                    color: 'success',
                };
            } catch (error) {
                console.error('Error updating athlete status:', error);
                this.snackbar = {
                    show: true,
                    text: 'Error updating athlete status',
                    color: 'error',
                };
            } finally {
                this.checkboxDisabled = null;
                this.loading = false;
                this.$emit('saving', false);
            }
        },

        async markResultCategoryAwarded(value) {
            // Make sure value is a boolean, not an event object
            const boolValue = typeof value === 'object' ? !this.resultCategory.awarded_at : Boolean(value);

            this.$emit('saving', true);
            this.loading = true;

            try {
                await ResultCategory.api().post(`/api/awards`, {
                    meet_id: this.resultCategory.meet_id,
                    model_type: 'App\\Models\\ResultCategory',
                    model_ids: [this.resultCategory.id],
                    field: 'awarded',
                    value: boolValue,
                });

                // Update local state
                this.resultCategory.awarded_at = boolValue ? new Date() : null;

                this.snackbar = {
                    show: true,
                    text: boolValue
                        ? `${this.resultCategory.description()} marked as awarded`
                        : `${this.resultCategory.description()} marked as not awarded`,
                    color: 'success',
                };
            } catch (error) {
                console.error('Error updating division status:', error);
                this.snackbar = {
                    show: true,
                    text: 'Error updating division status',
                    color: 'error',
                };
            } finally {
                this.loading = false;
                this.$emit('saving', false);
            }
        },

        async completeAwards() {
            // Mark all athletes as awarded first
            if (!this.allAwarded) {
                await this.checkAll('awarded', true);
            }

            // Then mark the category as awarded
            await this.markResultCategoryAwarded(true);
        },

        getResultStatusColor(result) {
            if (result.awarded_at) return 'success';
            if (result.award_prepared_at) return 'primary';
            return 'grey';
        },

        getResultStatusText(result) {
            if (result.awarded_at) return 'AWARDED';
            if (result.award_prepared_at) return 'CHECKED IN';
            return 'PENDING';
        },

        getAwardCount() {
            // Default award count logic (before the actual settings are loaded)
            const totalAthletes = this.resultCategory.results.length;

            // Check if the parent component provided award settings (injected or via props)
            if (this.$parent.$parent.getAwardCount) {
                return this.$parent.$parent.getAwardCount(this.resultCategory);
            }

            // Default fallback rules if settings are not available
            const rule = 'top3'; // Default rule

            switch (rule) {
                case 'top3':
                    return Math.min(3, totalAthletes);
                case 'top50plus1':
                    return Math.min(Math.ceil(totalAthletes / 2) + 1, totalAthletes);
                case 'top6':
                    return Math.min(6, totalAthletes);
                case 'top8':
                    return Math.min(8, totalAthletes);
                case 'top10':
                    return Math.min(10, totalAthletes);
                case 'all':
                    return totalAthletes;
                default:
                    return Math.min(3, totalAthletes);
            }
        },
    },
};
</script>

<style scoped>
.award-category-container {
    width: 100%;
}

.touch-friendly-header {
    min-height: 64px;
    padding: 16px;
    transition: background-color 0.3s ease;
}

.category-title {
    font-size: 1.1rem;
    font-weight: 500;
}

.touch-checkbox ::v-deep .v-input--selection-controls__ripple {
    transform: scale(1.4);
}

.touch-checkbox ::v-deep .v-input--selection-controls__input {
    transform: scale(1.2);
    margin-right: 12px;
}

.award-progress {
    opacity: 0.8;
}

.awarded-message {
    border-radius: 4px;
    background-color: rgba(76, 175, 80, 0.03);
    border: 1px dashed rgba(76, 175, 80, 0.3);
}

.award-table {
    margin-top: 12px;
}

.award-table ::v-deep tbody tr {
    height: 56px !important;
    transition: background-color 0.3s ease;
}

.award-table ::v-deep tbody td {
    height: 56px;
    font-size: 1rem;
    padding: 0 12px;
}

.award-table ::v-deep tbody tr:nth-child(1),
.award-table ::v-deep tbody tr:nth-child(2),
.award-table ::v-deep tbody tr:nth-child(3) {
    background-color: rgba(255, 248, 225, 0.3);
}

.placement-number {
    font-size: 1.15rem;
    font-weight: 500;
}

.action-btn {
    transition: all 0.3s ease;
}

.awarded-table {
    opacity: 0.85;
}

/* iPad specific optimizations */
@media only screen and (min-width: 768px) and (max-width: 1024px) {
    .award-table ::v-deep tbody td {
        height: 64px;
    }
}
</style>
